"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import styles from "./css/consult.module.css"
import Pagination from "../../../../components/Pagination"
import Button from "../../../../components/Button"
import { updateConsultation } from "../../../../api/clinic/consultationApi.js"

export default function Consult({ 
  patientName = "Ryan Morris", 
  patientIdNumber = "",
  consultationId, 
  patientData = {}, 
  serviceName,
  initialData,
  onDataChange,
  ensureConsultationExists,
  onSave, 
  onCreateScript, 
  showInjectionSection = false 
}) {
  const navigate = useNavigate()
  
  // Get service type from database - prepopulate with actual service name
  const getInitialServiceType = () => {
    if (initialData?.serviceType) return initialData.serviceType;
    // Return the actual service name from database or a default message
    return serviceName || "No Service Selected";
  };
  
  const [serviceType, setServiceType] = useState(getInitialServiceType())
  const [consultationNotes, setConsultationNotes] = useState(initialData?.consultationNotes || "")
  const [currentMedicationPage, setCurrentMedicationPage] = useState(1)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState("")
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [hasSavedData, setHasSavedData] = useState(!!initialData?.consultationNotes)
 
  const hasValidConsultationNotes = consultationNotes.trim().length > 0
 
  const notifyChange = () => {
    if (onDataChange) onDataChange()
    setHasUnsavedChanges(true)
  }

  const [injectionMedications, setInjectionMedications] = useState(
    initialData?.injectionMedications || [
      {
        id: 1,
        medicationName: "",
        indication: "",
        dosage: "",
        unit: "",
        frequency: "",
        customFrequency: "",
        notes: "",
      },
    ]
  )

  const handleAddInjectionMedication = () => {
    const newId = Math.max(...injectionMedications.map((m) => m.id)) + 1
    const newMedication = {
      id: newId,
      medicationName: "",
      indication: "",
      dosage: "",
      unit: "",
      frequency: "",
      customFrequency: "",
      notes: "",
    }
    setInjectionMedications([...injectionMedications, newMedication])
    setCurrentMedicationPage(injectionMedications.length + 1)
    notifyChange()
  }

  const handleInjectionMedicationChange = (id, field, value) => {
    setInjectionMedications(injectionMedications.map((med) => (med.id === id ? { ...med, [field]: value } : med)))
    notifyChange()
  }

  const currentMedication = injectionMedications[currentMedicationPage - 1]

  const shouldShowInjectionSection = showInjectionSection || (serviceType && serviceType.toLowerCase().includes('injection'))

  // Format patient name for new script form: "Name Initials (ProfileNumber)"
  const formatPatientForScript = () => {
    if (!patientName) return ""
    
    // Split name into parts and get initials
    const nameParts = patientName.trim().split(' ')
    if (nameParts.length < 2) return patientName
    
    const lastName = nameParts[nameParts.length - 1]
    const firstNames = nameParts.slice(0, -1)
    const initials = firstNames.map(name => name.charAt(0).toUpperCase()).join('')
    
    const profileNumber = patientData?.profilenumber || patientIdNumber || ''
    
    return `${lastName} ${initials} (${profileNumber})`
  }

  const handleSave = async () => {
    try {
      if (!hasValidConsultationNotes) return

      setSaving(true)
      setSaveError("")
      setSaveSuccess(false)

      // Ensure consultation exists (creates if needed)
      const activeConsultationId = await ensureConsultationExists()

      // Prepare consultation notes payload
      const payload = {
        consultationnotes: consultationNotes || null,
      };
      
      // If service type contains Injection, add injection medication data
      if (serviceType && serviceType.toLowerCase().includes('injection') && injectionMedications.length > 0) {
        // Filter out empty medications
        const validMedications = injectionMedications.filter(med => 
          med.medicationName || med.indication || med.dosage || med.unit || med.frequency || med.customFrequency || med.notes
        );
        
        if (validMedications.length > 0) {
          payload.injectionmedicationname = validMedications.map(m => m.medicationName || "");
          payload.injectionindication = validMedications.map(m => m.indication || "");
          payload.injectiondosage = validMedications.map(m => m.dosage || "");
          payload.injectionunit = validMedications.map(m => m.unit || "");
          payload.injectionfrequency = validMedications.map(m => {
            if (m.frequency === "Other" && m.customFrequency) {
              return m.customFrequency;
            }
            return m.frequency || "";
          });
          payload.injectionnotes = validMedications.map(m => m.notes || "");
        }
      }

      await updateConsultation(activeConsultationId, payload)

      setSaveSuccess(true)
      setHasUnsavedChanges(false)
      // Mark as having saved data if consultation notes were saved
      if (consultationNotes && consultationNotes.trim()) {
        setHasSavedData(true)
      }
      setTimeout(() => setSaveSuccess(false), 3000)
      
      if (onSave) {
        onSave({ serviceType, consultationNotes, injectionMedications })
      }
    } catch (e) {
      setSaveError(e.message || "Failed to save data")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerMain}>
          <h2 className={styles.patientName}>
            {shouldShowInjectionSection ? `${patientName}(${patientIdNumber})` : patientName}
          </h2>

          <div className={styles.serviceTypeContainer}>
            <label className={styles.serviceLabel}>Type of Service</label>
            <input
              type="text"
              className={styles.serviceInput}
              value={serviceType}
              onChange={(e) => { setServiceType(e.target.value); notifyChange(); }}
              readOnly
            />
          </div>
        </div>

        <div className={styles.headerControls}>
          <Button
            className={styles.guidelineButton}
            onClick={() => navigate('/customer/clinic/doh-guideline/family-planning')}
          >
            DoH Guideline
          </Button>
        </div>
      </div>

      <div className={styles.notesSection}>
        <h3 className={styles.notesTitle}>Patient Consultation Notes</h3>
        <textarea
          className={styles.notesTextarea}
          placeholder="Input text"
          value={consultationNotes}
          onChange={(e) => { setConsultationNotes(e.target.value); notifyChange(); }}
          rows={3}
        />
      </div>

      {shouldShowInjectionSection && (
        <div className={styles.injectionSection}>
          <h3 className={styles.injectionTitle}>Injection Medication Used</h3>

          {currentMedication && (
            <div key={currentMedication.id} className={styles.injectionForm}>
              <div className={styles.injectionRow}>
                <div className={styles.injectionField}>
                  <label>Medication Name</label>
                  <select
                    value={currentMedication.medicationName}
                    onChange={(e) =>
                      handleInjectionMedicationChange(currentMedication.id, "medicationName", e.target.value)
                    }
                    className={styles.injectionSelect}
                  >
                    <option value="">Enter Drug Name</option>
                    <option value="Insulin">Insulin</option>
                    <option value="Morphine">Morphine</option>
                    <option value="Epinephrine">Epinephrine</option>
                  </select>
                </div>

                <div className={styles.injectionField}>
                  <label>Indication</label>
                  <input
                    type="text"
                    value={currentMedication.indication}
                    onChange={(e) =>
                      handleInjectionMedicationChange(currentMedication.id, "indication", e.target.value)
                    }
                    className={styles.injectionInput}
                  />
                </div>

                <div className={styles.injectionField}>
                  <label>Dosage</label>
                  <input
                    type="text"
                    placeholder="Enter Dosage"
                    value={currentMedication.dosage}
                    onChange={(e) => handleInjectionMedicationChange(currentMedication.id, "dosage", e.target.value)}
                    className={styles.injectionInput}
                  />
                </div>

                <div className={styles.injectionField}>
                  <label>Unit</label>
                  <select
                    value={currentMedication.unit}
                    onChange={(e) => handleInjectionMedicationChange(currentMedication.id, "unit", e.target.value)}
                    className={styles.injectionSelect}
                  >
                    <option value="">Select Unit</option>
                    <option value="mg">mg</option>
                    <option value="ml">ml</option>
                    <option value="units">units</option>
                  </select>
                </div>

                <div className={styles.injectionField}>
                  <label>Frequency</label>
                  <select
                    value={currentMedication.frequency}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInjectionMedicationChange(currentMedication.id, "frequency", value);
                      // Clear custom frequency when not "Other"
                      if (value !== "Other") {
                        handleInjectionMedicationChange(currentMedication.id, "customFrequency", "");
                      }
                    }}
                    className={styles.injectionSelect}
                  >
                    <option value="">Select Frequency</option>
                    <option value="Once daily">Once daily</option>
                    <option value="Twice daily">Twice daily</option>
                    <option value="Three times daily">Three times daily</option>
                    <option value="Other">Other</option>
                  </select>
                  {currentMedication.frequency === "Other" && (
                    <input
                      type="text"
                      placeholder="Enter frequency"
                      value={currentMedication.customFrequency}
                      onChange={(e) => handleInjectionMedicationChange(currentMedication.id, "customFrequency", e.target.value)}
                      className={styles.injectionInput}
                      style={{ marginTop: "8px" }}
                    />
                  )}
                </div>
              </div>

              <div className={styles.injectionNotesField}>
                <label>Notes</label>
                <textarea
                  value={currentMedication.notes}
                  onChange={(e) => handleInjectionMedicationChange(currentMedication.id, "notes", e.target.value)}
                  className={styles.injectionNotesTextarea}
                  rows={3}
                />
              </div>
            </div>
          )}

          <div className={styles.injectionControls}>
            <button className={styles.addButton} onClick={handleAddInjectionMedication}>
              Add
            </button>

            <Pagination
              currentPage={currentMedicationPage}
              totalPages={injectionMedications.length}
              onPageChange={setCurrentMedicationPage}
            />
          </div>
        </div>
      )}

      {saveError && (
        <div className={styles.inlineError}>
          {saveError}
        </div>
      )}

      {saveSuccess && (
        <div className={styles.successMessage}>
          Data saved successfully!
        </div>
      )}

      <div className={styles.saveContainer}>
        <button className={styles.saveButton} onClick={handleSave} disabled={saving || !hasValidConsultationNotes}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      
          <Button
            className={styles.createScriptButton}
            type="button"
            variant="primary"
            disabled={saving}
            onClick={(e) => {
              e.preventDefault()
              console.log("[v0] Create Script button clicked in consult component")

              // All roles use the existing callback (PCDT included)
              if (onCreateScript) {
                onCreateScript()
              } else {
                console.log("[v0] onCreateScript callback is not defined")
              }
            }}
          >
            Create Script
          </Button>
        
      </div>
    </div>
  )
}